import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import BulkUploadModal from '../components/BulkUploadModal';
import axios from 'axios';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import {
  Plus, Search, X, Save, Loader2, Download, Upload,
  CheckCircle2, XCircle, DollarSign, CreditCard,
  ArrowUpRight, Clock, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LoansAdmin = () => {
  const [loans, setLoans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRecovery, setShowRecovery] = useState(null);
  const [recoveryAmt, setRecoveryAmt] = useState('');
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Stage 73.20x — Manager partial-approval modal state
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveCtx, setApproveCtx] = useState(null);
  const [approveLoadingCtx, setApproveLoadingCtx] = useState(false);
  const [approveAmt, setApproveAmt] = useState('');
  const [approveRemarks, setApproveRemarks] = useState('');
  const [approveSubmitting, setApproveSubmitting] = useState(false);

  const [form, setForm] = useState({
    employee_id: '', company_id: '', loan_type: 'salary_advance',
    principal_amount: '', interest_rate: '0', tenure_months: '12',
    emi_amount: '', disbursement_date: '', recovery_start_date: '',
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      if (data.length > 0 && !selectedCompany) { setSelectedCompany(data[0].id); setForm(f => ({ ...f, company_id: data[0].id })); }
    } catch (e) { console.error(e); }
  }, [selectedCompany, setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchLoans = useCallback(async () => {
    setLoading(true);
    try {
      const params = { company_id: selectedCompany };
      if (statusFilter !== 'all') params.status = statusFilter;
      const [loansRes, empRes] = await Promise.all([
        axios.get(`${API_URL}/payroll/loans`, { params, withCredentials: true }),
        axios.get(`${API_URL}/employees?company_id=${selectedCompany}`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setLoans(loansRes.data);
      setEmployees(empRes.data || []);
    } catch (e) { console.error(e); setLoans([]); }
    finally { setLoading(false); }
  }, [selectedCompany, statusFilter]);

  useEffect(() => { if (selectedCompany) fetchLoans(); }, [selectedCompany, statusFilter, fetchLoans]);



  const getEmpName = (eid) => {
    const emp = employees.find(e => e.id === eid);
    return emp ? `${emp.personal_details?.first_name || ''} ${emp.personal_details?.last_name || ''}` : eid;
  };

  const handleCreate = async () => {
    if (!form.employee_id || !form.principal_amount || !form.emi_amount || !form.disbursement_date || !form.recovery_start_date) {
      toast.error('Fill all required fields'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/payroll/loans`, {
        ...form,
        company_id: selectedCompany,
        principal_amount: parseFloat(form.principal_amount),
        interest_rate: parseFloat(form.interest_rate || '0'),
        tenure_months: parseInt(form.tenure_months || '12'),
        emi_amount: parseFloat(form.emi_amount),
        status: 'pending',
      }, { withCredentials: true });
      toast.success('Advance created');
      setShowModal(false);
      setForm({ employee_id: '', company_id: selectedCompany, loan_type: 'salary_advance', principal_amount: '', interest_rate: '0', tenure_months: '12', emi_amount: '', disbursement_date: '', recovery_start_date: '' });
      fetchLoans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleAction = async (loanId, action) => {
    // Stage 73.20x — Manager partial-approval flow
    if (action === 'approve') {
      const loan = loans.find(l => l.id === loanId);
      if (!loan) { toast.error('Advance not found'); return; }
      setApproveTarget(loan);
      setApproveAmt(String(loan.principal_amount || ''));
      setApproveRemarks('');
      setApproveCtx(null);
      setApproveLoadingCtx(true);
      try {
        const { data } = await axios.get(`${API_URL}/payroll/loans/${loanId}/approval-context`, { withCredentials: true });
        setApproveCtx(data);
      } catch (e) {
        toast.error('Failed to load approval context: ' + (e?.response?.data?.detail || e.message));
      } finally {
        setApproveLoadingCtx(false);
      }
      return;
    }
    try {
      await axios.post(`${API_URL}/payroll/loans/${loanId}/${action}`, {}, { withCredentials: true });
      toast.success(`Loan ${action}d`);
      fetchLoans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const submitApproval = async () => {
    if (!approveTarget) return;
    const amt = parseFloat(approveAmt);
    const requested = parseFloat(approveTarget.principal_amount || 0);
    if (!amt || amt <= 0) { toast.error('Approved amount must be > 0. Use Reject to decline.'); return; }
    if (amt > requested + 0.01) { toast.error(`Approved amount cannot exceed requested ₹${requested.toLocaleString()}.`); return; }
    const isPartial = amt + 0.01 < requested;
    if (isPartial && !approveRemarks.trim()) { toast.error('Remarks are required for partial approval.'); return; }
    setApproveSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/payroll/loans/${approveTarget.id}/approve`,
        { approved_amount: amt, remarks: approveRemarks.trim() },
        { withCredentials: true },
      );
      if (data?.is_partial) {
        toast.success(`Partially approved ₹${amt.toLocaleString()} of ₹${requested.toLocaleString()}`);
      } else {
        toast.success('Advance approved');
      }
      setApproveTarget(null);
      setApproveCtx(null);
      fetchLoans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    } finally {
      setApproveSubmitting(false);
    }
  };

  const handleRecovery = async (loanId) => {
    if (!recoveryAmt || parseFloat(recoveryAmt) <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await axios.post(`${API_URL}/payroll/loans/${loanId}/record-recovery`, {
        loan_id: loanId,
        employee_id: showRecovery.employee_id,
        recovery_date: new Date().toISOString().split('T')[0],
        amount: parseFloat(recoveryAmt),
        payroll_period: 'manual',
      }, { withCredentials: true });
      toast.success('Recovery recorded');
      setShowRecovery(null);
      setRecoveryAmt('');
      fetchLoans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const statusBadge = (status) => {
    const map = {
      active: 'bg-green-50 text-green-700 border-green-200',
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      applied: 'bg-blue-50 text-blue-700 border-blue-200',
      closed: 'bg-slate-100 text-slate-600 border-slate-200',
      rejected: 'bg-red-50 text-red-700 border-red-200',
    };
    return map[status] || 'bg-slate-50 text-slate-600';
  };

  const filtered = loans.filter(l =>
    !search || getEmpName(l.employee_id).toLowerCase().includes(search.toLowerCase()) || l.loan_type?.toLowerCase().includes(search.toLowerCase())
  );

  const totalOutstanding = filtered.reduce((s, l) => s + (l.outstanding_amount || 0), 0);
  const totalDisbursed = filtered.reduce((s, l) => s + (l.principal_amount || 0), 0);

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="loans-admin-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <CreditCard className="inline-block w-8 h-8 mr-2 mb-1" /> Advances Administration
            </h1>
            <p className="mt-1 text-sm text-slate-600">Manage employee advances, approvals, EMI tracking, and recoveries</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" data-testid="loans-bulk-upload-btn" onClick={() => setShowBulkUpload(true)}>
              <Upload className="w-4 h-4 mr-2" /> Bulk Upload
            </Button>
            <Button variant="outline" data-testid="loans-export-btn" onClick={() => {
              if (filtered.length === 0) { toast.error('No advances to export'); return; }
              downloadCSV(filtered.map(l => ({
                Employee: getEmpName(l.employee_id), Type: l.loan_type, Principal: l.principal_amount,
                EMI: l.emi_amount, Outstanding: l.outstanding_amount, Recovered: l.total_recovered, Status: l.status
              })), 'loans_export.csv');
              toast.success('Advances exported');
            }}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button data-testid="new-loan-btn" onClick={() => setShowModal(true)} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
              <Plus className="w-4 h-4 mr-2" /> New Advance
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} data-testid="loan-company-select"
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="loan-status-filter"
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            <option value="rejected">Rejected</option>
          </select>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input data-testid="loan-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search employee or type..." className="w-full h-10 pl-10 pr-4 bg-white border border-slate-300 rounded-md text-sm" />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Total Advances</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono">{filtered.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Active</p>
            <p className="text-2xl font-semibold text-green-600 mt-1 font-mono">{filtered.filter(l => l.status === 'active').length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Total Disbursed</p>
            <p className="text-2xl font-semibold text-blue-600 mt-1 font-mono">{totalDisbursed.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase text-slate-500">Outstanding</p>
            <p className="text-2xl font-semibold text-red-600 mt-1 font-mono">{totalOutstanding.toLocaleString()}</p>
          </div>
        </div>

        {/* Loans Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full" data-testid="loans-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Employee</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Type</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Principal</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">EMI</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Outstanding</th>
                <th className="text-center py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Recoveries</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-12 text-slate-500">
                  <CreditCard className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No advances found</p>
                </td></tr>
              ) : filtered.map(l => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`loan-row-${l.id}`}>
                  <td className="py-3 px-4 text-sm font-medium text-slate-900">{getEmpName(l.employee_id)}</td>
                  <td className="py-3 px-4 text-sm text-slate-600 capitalize">{l.loan_type?.replace(/_/g, ' ')}</td>
                  <td className="py-3 px-4 text-sm text-right font-mono">{(l.principal_amount || 0).toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-right font-mono">{(l.emi_amount || 0).toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-right font-mono font-semibold text-red-600">{(l.outstanding_amount || 0).toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-center font-mono">{l.recoveries_done || 0}/{l.tenure_months || 0}</td>
                  <td className="py-3 px-4 text-sm">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(l.status)}`}>{l.status}</span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex gap-1 justify-end">
                      <button
                        data-testid={`loan-approval-pdf-${l.id}`}
                        onClick={async () => {
                          try {
                            const res = await axios.get(`${API_URL}/payroll/loans/${l.id}/approval-form-pdf`, { withCredentials: true, responseType: 'blob' });
                            const url = URL.createObjectURL(res.data);
                            const a = document.createElement('a');
                            a.href = url; a.download = `loan_approval_${l.id.slice(0, 8)}.pdf`; a.click();
                            URL.revokeObjectURL(url);
                          } catch (e) {
                            toast.error('PDF failed: ' + (e?.response?.data?.detail || e.message));
                          }
                        }}
                        className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-xs font-medium" title="Approval Form PDF">
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      {(l.status === 'pending' || l.status === 'applied') && (
                        <>
                          <button data-testid={`approve-loan-${l.id}`} onClick={() => handleAction(l.id, 'approve')}
                            className="px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded text-xs font-medium" title="Approve">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`reject-loan-${l.id}`} onClick={() => handleAction(l.id, 'reject')}
                            className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 rounded text-xs font-medium" title="Reject">
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      {l.status === 'active' && (
                        <>
                          <button data-testid={`recover-loan-${l.id}`} onClick={() => { setShowRecovery(l); setRecoveryAmt(String(l.emi_amount || '')); }}
                            className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-xs font-medium" title="Record Recovery">
                            <DollarSign className="w-3.5 h-3.5" />
                          </button>
                          <button data-testid={`close-loan-${l.id}`} onClick={() => handleAction(l.id, 'close')}
                            className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-xs font-medium" title="Close">
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Create Loan Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-loan-modal">
            <div className="bg-white rounded-lg w-full max-w-lg">
              <div className="flex items-center justify-between p-5 border-b">
                <h2 className="text-lg font-semibold">Create New Advance</h2>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Employee *</label>
                  <select data-testid="loan-employee-select" value={form.employee_id} onChange={e => setForm({...form, employee_id: e.target.value})}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                    <option value="">Select Employee</option>
                    {employees.map(emp => <option key={emp.id} value={emp.id}>{`${emp.personal_details?.first_name} ${emp.personal_details?.last_name} (${emp.employment_details?.employee_code})`}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Advance Type</label>
                    <select data-testid="loan-type-select" value={form.loan_type} onChange={e => setForm({...form, loan_type: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="salary_advance">Salary Advance</option>
                      <option value="personal_loan">Personal Advance</option>
                      <option value="emergency_loan">Emergency Advance</option>
                      <option value="housing_loan">Housing Advance</option>
                      <option value="vehicle_loan">Vehicle Advance</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Principal Amount *</label>
                    <input data-testid="loan-principal" type="number" value={form.principal_amount}
                      onChange={e => setForm({...form, principal_amount: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="50000" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Interest Rate (%)</label>
                    <input data-testid="loan-interest" type="number" step="0.1" value={form.interest_rate}
                      onChange={e => setForm({...form, interest_rate: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Tenure (months)</label>
                    <input data-testid="loan-tenure" type="number" value={form.tenure_months}
                      onChange={e => setForm({...form, tenure_months: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">EMI Amount *</label>
                    <input data-testid="loan-emi" type="number" value={form.emi_amount}
                      onChange={e => setForm({...form, emi_amount: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="4200" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Disbursement Date *</label>
                    <input data-testid="loan-disbursement" type="date" value={form.disbursement_date}
                      onChange={e => setForm({...form, disbursement_date: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Recovery Start Date *</label>
                    <input data-testid="loan-recovery-start" type="date" value={form.recovery_start_date}
                      onChange={e => setForm({...form, recovery_start_date: e.target.value})}
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-5 border-t">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button data-testid="save-loan-btn" onClick={handleCreate} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Create Advance
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Recovery Modal */}
        {showRecovery && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="recovery-modal">
            <div className="bg-white rounded-lg w-full max-w-sm">
              <div className="flex items-center justify-between p-5 border-b">
                <h2 className="text-lg font-semibold">Record Recovery</h2>
                <button onClick={() => setShowRecovery(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-sm text-slate-600">Employee: <strong>{getEmpName(showRecovery.employee_id)}</strong></p>
                <p className="text-sm text-slate-600">Outstanding: <strong className="text-red-600">{(showRecovery.outstanding_amount || 0).toLocaleString()}</strong></p>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Recovery Amount *</label>
                  <input data-testid="recovery-amount" type="number" value={recoveryAmt} onChange={e => setRecoveryAmt(e.target.value)}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-2 p-5 border-t">
                <Button variant="outline" onClick={() => setShowRecovery(null)}>Cancel</Button>
                <Button data-testid="submit-recovery-btn" onClick={() => handleRecovery(showRecovery.id)} className="bg-green-600 hover:bg-green-700 text-white">
                  <DollarSign className="w-4 h-4 mr-2" /> Record
                </Button>
              </div>
            </div>
          </div>
        )}

        <BulkUploadModal
          open={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          title="Bulk Advance Upload"
          description="Upload a CSV to create multiple advances at once."
          templateUrl="/payroll/loans/bulk-upload/template"
          uploadUrl="/payroll/loans/bulk-upload"
          uploadParams={{ company_id: selectedCompany }}
          templateFileName="loans_bulk_upload_template.csv"
          onUploadSuccess={() => fetchLoans()}
          columns={['employee_code', 'loan_type', 'principal_amount', 'interest_rate', 'tenure_months', 'emi_amount', 'disbursement_date', 'recovery_start_date']}
        />

        {/* Stage 73.20x — Manager partial-approval modal */}
        {approveTarget && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="approval-modal">
            <div className="bg-white rounded-lg w-full max-w-xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-5 border-b">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Review &amp; Approve Advance</h2>
                  <p className="text-xs text-slate-500 mt-0.5">Manager sees pending exposure before deciding the approved amount.</p>
                </div>
                <button onClick={() => { setApproveTarget(null); setApproveCtx(null); }} className="text-slate-400 hover:text-slate-600" data-testid="approval-modal-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {approveLoadingCtx ? (
                  <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : (
                  <>
                    {/* Employee header */}
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="approval-employee-card">
                      <p className="text-xs uppercase text-slate-500 font-semibold">Employee</p>
                      <p className="text-base font-semibold text-slate-900 mt-0.5">
                        {approveCtx?.employee?.employee_name || getEmpName(approveTarget.employee_id)}
                        <span className="text-slate-500 font-normal ml-2">({approveCtx?.employee?.employee_code || '—'})</span>
                      </p>
                      {approveCtx?.employee?.designation && (
                        <p className="text-xs text-slate-500 mt-0.5">{approveCtx.employee.designation} {approveCtx?.employee?.department ? `· ${approveCtx.employee.department}` : ''}</p>
                      )}
                    </div>

                    {/* Pending advance balance banner — the headline manager-visibility */}
                    <div
                      className={
                        (approveCtx?.pending_balance_total || 0) > 0
                          ? 'border-2 border-amber-300 bg-amber-50 rounded-md p-4'
                          : 'border-2 border-emerald-300 bg-emerald-50 rounded-md p-4'
                      }
                      data-testid="pending-balance-banner"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-xs font-semibold uppercase text-slate-600">Existing Open Advance Balance</p>
                          <p
                            className={`text-3xl font-bold font-mono mt-1 ${(approveCtx?.pending_balance_total || 0) > 0 ? 'text-amber-700' : 'text-emerald-700'}`}
                            data-testid="pending-balance-amount"
                          >
                            ₹{Number(approveCtx?.pending_balance_total || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-slate-600 mt-1">
                            {approveCtx?.open_advances_count > 0
                              ? `${approveCtx.open_advances_count} advance${approveCtx.open_advances_count > 1 ? 's' : ''} currently active / disbursed`
                              : 'No other open advances'}
                          </p>
                        </div>
                        {(approveCtx?.pending_balance_total || 0) > 0 && (
                          <AlertTriangle className="w-7 h-7 text-amber-600 flex-shrink-0" />
                        )}
                      </div>
                      {(approveCtx?.open_advances || []).length > 0 && (
                        <div className="mt-3 border-t border-amber-200 pt-2 space-y-1" data-testid="open-advances-list">
                          {(approveCtx.open_advances || []).slice(0, 5).map((a, i) => (
                            <div key={a.advance_id} className="flex items-center justify-between text-xs">
                              <span className="text-slate-700 capitalize">{(a.loan_type || '').replace(/_/g, ' ')}</span>
                              <span className="text-slate-700 font-mono">₹{Number(a.outstanding_amount).toLocaleString('en-IN')} pending</span>
                            </div>
                          ))}
                          {(approveCtx.open_advances || []).length > 5 && (
                            <p className="text-xs text-slate-500 italic">+ {approveCtx.open_advances.length - 5} more...</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Requested vs. Approve */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="border border-slate-200 rounded-md p-3 bg-white">
                        <p className="text-xs font-semibold uppercase text-slate-500">Requested Amount</p>
                        <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono" data-testid="requested-amount">₹{Number(approveTarget.principal_amount || 0).toLocaleString('en-IN')}</p>
                        <p className="text-xs text-slate-500 mt-1">{approveTarget.tenure_months || 0} months @ ₹{Number(approveTarget.emi_amount || 0).toLocaleString('en-IN')}/EMI</p>
                      </div>
                      <div className="border-2 border-blue-200 rounded-md p-3 bg-blue-50">
                        <label className="text-xs font-semibold uppercase text-blue-700">Approve Amount *</label>
                        <input
                          type="number"
                          data-testid="approve-amount-input"
                          value={approveAmt}
                          min="0"
                          max={approveTarget.principal_amount}
                          step="any"
                          onChange={e => setApproveAmt(e.target.value)}
                          className="w-full mt-1 px-2 py-1.5 border border-blue-300 rounded text-2xl font-mono font-semibold text-blue-900 bg-white"
                        />
                        <p className="text-xs text-blue-700 mt-1">EMI will be ₹{((parseFloat(approveAmt || 0)) / (approveTarget.tenure_months || 1)).toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Live partial-approval indicator */}
                    {parseFloat(approveAmt || 0) > 0 && parseFloat(approveAmt) + 0.01 < parseFloat(approveTarget.principal_amount || 0) && (
                      <div className="border border-rose-300 bg-rose-50 rounded-md p-3 text-sm" data-testid="partial-warning">
                        <p className="font-semibold text-rose-700">
                          Partial approval: reducing by ₹{(parseFloat(approveTarget.principal_amount) - parseFloat(approveAmt || 0)).toLocaleString('en-IN')}
                        </p>
                        <p className="text-xs text-rose-600 mt-0.5">Remarks are mandatory below.</p>
                      </div>
                    )}

                    {/* Remarks */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        Approval Remarks {parseFloat(approveAmt || 0) + 0.01 < parseFloat(approveTarget.principal_amount || 0) && <span className="text-rose-600">*</span>}
                      </label>
                      <textarea
                        data-testid="approve-remarks-input"
                        rows={3}
                        value={approveRemarks}
                        onChange={e => setApproveRemarks(e.target.value)}
                        placeholder="e.g. Existing pending advance ₹4,000 — approving only the differential ₹6,000"
                        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                      />
                    </div>

                    {/* Prior approval history (if any) */}
                    {(approveCtx?.approval_history || []).length > 0 && (
                      <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                        <p className="text-xs font-semibold uppercase text-slate-600 mb-2">Prior Approval History</p>
                        <div className="space-y-1.5 text-xs">
                          {(approveCtx.approval_history || []).map((h, i) => (
                            <div key={i} className="flex justify-between border-b border-slate-200 pb-1 last:border-0">
                              <div>
                                <p className="text-slate-800 font-medium">{h.by_name}</p>
                                <p className="text-slate-500">{h.remarks || '—'}</p>
                              </div>
                              <div className="text-right font-mono text-slate-700">
                                <p>₹{Number(h.approved_amount).toLocaleString('en-IN')}</p>
                                {h.partial_reduction > 0 && <p className="text-rose-600">−₹{Number(h.partial_reduction).toLocaleString('en-IN')}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="flex justify-end gap-2 p-5 border-t bg-slate-50">
                <Button variant="outline" data-testid="approve-cancel-btn" onClick={() => { setApproveTarget(null); setApproveCtx(null); }}>Cancel</Button>
                <Button data-testid="approve-submit-btn" onClick={submitApproval} disabled={approveSubmitting || approveLoadingCtx}
                  className="bg-green-600 hover:bg-green-700 text-white">
                  {approveSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Approve ₹{Number(approveAmt || 0).toLocaleString('en-IN')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default LoansAdmin;
